/**
 * FunnelsEventsDashboard (V35.5)
 *
 * Affiche les événements custom (`analytics_events`) + 2 funnels (Inscription, Adhésion FTPE)
 * avec taux de conversion à chaque étape.
 *
 * Source : GET /api/admin/analytics/events/summary?days=N
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, RefreshCw, Loader2, BarChart3, Users, MousePointerClick, UserPlus, Building2, Sparkles, ArrowDown, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PERIODS = [
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: '30 derniers jours' },
  { value: '90', label: '90 derniers jours' },
];

const FUNNEL_LABELS = {
  // Inscription
  'page.register_view': "Vue page d'inscription",
  'conversion.register_submit': 'Soumission du formulaire',
  'conversion.register_success': 'Inscription réussie',
  // FTPE
  'cta.ftpe_adherer': 'Clic « Adhérer »',
  'conversion.ftpe_payment_started': 'Paiement initié',
  'conversion.ftpe_payment_success': 'Paiement validé',
};

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// ---------- Helpers ----------
function calcConvRate(current, previous) {
  if (!previous || previous === 0) return null;
  return Math.round((current / previous) * 1000) / 10; // 1 décimale
}

// ---------- Funnel vertical ----------
function FunnelChart({ title, icon, steps }) {
  if (!steps || steps.length === 0) return null;
  const maxCount = Math.max(...steps.map((s) => s.count), 1);

  return (
    <Card className="dark:bg-slate-900 dark:border-slate-800">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {steps.map((step, idx) => {
          const label = FUNNEL_LABELS[step.step] || step.step;
          const prev = idx > 0 ? steps[idx - 1].count : null;
          const conv = idx > 0 ? calcConvRate(step.count, prev) : null;
          const widthPct = Math.max((step.count / maxCount) * 100, 3);

          return (
            <div key={step.step}>
              <div className="flex items-center justify-between text-sm mb-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="font-medium text-gray-700 dark:text-gray-200">{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {conv !== null && (
                    <Badge
                      variant="outline"
                      className={
                        conv >= 60
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
                          : conv >= 30
                          ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700'
                          : 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
                      }
                    >
                      {conv}% conv.
                    </Badge>
                  )}
                  <span className="font-bold text-gray-900 dark:text-white w-12 text-right">{step.count}</span>
                </div>
              </div>
              <div className="relative h-7 bg-gray-100 dark:bg-slate-800 rounded-md overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-400 dark:to-indigo-500 rounded-md transition-all duration-700"
                  style={{ width: `${widthPct}%` }}
                />
                <div className="absolute inset-0 flex items-center px-2 text-xs text-white/90 font-medium pointer-events-none">
                  {step.count > 0 && idx > 0 && (
                    <span className="flex items-center gap-1">
                      <ArrowDown className="w-3 h-3" />
                      {prev - step.count} drop-offs
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {/* Conversion totale */}
        {steps.length > 1 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400 font-medium">Conversion globale</span>
            <Badge className="bg-blue-600 text-white font-bold text-base">
              {calcConvRate(steps[steps.length - 1].count, steps[0].count) ?? 0}%
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Composant principal ----------
export default function FunnelsEventsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/analytics/events/summary`, {
        headers: authHeaders(),
        params: { days: period },
      });
      setData(res.data?.events || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de chargement des analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const totalEvents = data?.total_events || 0;
  const topEvents = data?.top_events || [];
  const funnelRegister = data?.funnel_register || [];
  const funnelFtpe = data?.funnel_ftpe_adhesion || [];
  const maxTopCount = Math.max(...topEvents.map((e) => e.count), 1);

  return (
    <div className="space-y-5" data-testid="funnels-events-dashboard">
      <Card className="dark:bg-slate-900 dark:border-slate-800">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Funnels & Événements custom
              </CardTitle>
              <CardDescription className="dark:text-gray-400">
                Suivi des clics CTA et tunnels de conversion (Inscription, Adhésion FTPE).
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-48" data-testid="period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={fetchSummary} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                <MousePointerClick className="w-3.5 h-3.5" /> Total événements
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{totalEvents}</p>
            </div>
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                <Sparkles className="w-3.5 h-3.5" /> Types d&apos;événements
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{topEvents.length}</p>
            </div>
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                <UserPlus className="w-3.5 h-3.5" /> Inscriptions réussies
              </div>
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                {funnelRegister.find((s) => s.step === 'conversion.register_success')?.count || 0}
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                <Building2 className="w-3.5 h-3.5" /> Adhésions FTPE payées
              </div>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {funnelFtpe.find((s) => s.step === 'conversion.ftpe_payment_success')?.count || 0}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Funnels */}
      <div className="grid lg:grid-cols-2 gap-5">
        <FunnelChart
          title="Funnel Inscription"
          icon={<UserPlus className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
          steps={funnelRegister}
        />
        <FunnelChart
          title="Funnel Adhésion FTPE"
          icon={<Building2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />}
          steps={funnelFtpe}
        />
      </div>

      {/* Top events */}
      <Card className="dark:bg-slate-900 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            Top événements (toutes catégories)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-gray-400" />
            </div>
          )}
          {!loading && topEvents.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Activity className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p>Aucun événement custom enregistré sur cette période.</p>
              <p className="text-xs mt-1">
                Les événements seront capturés via le hook <code>useTrackEvent</code> côté frontend.
              </p>
            </div>
          )}
          <div className="space-y-2">
            {topEvents.map((ev) => {
              const widthPct = Math.max((ev.count / maxTopCount) * 100, 3);
              return (
                <div key={ev.event_name} className="flex items-center gap-3 group">
                  <div className="w-64 flex-shrink-0">
                    <code className="text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">
                      {ev.event_name}
                    </code>
                  </div>
                  <div className="flex-1 relative h-7 bg-gray-100 dark:bg-slate-800 rounded-md overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-md transition-all duration-700"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                  <div className="text-right w-28 flex-shrink-0 text-sm">
                    <div className="font-bold text-gray-900 dark:text-white">{ev.count}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-end gap-1">
                      <Users className="w-3 h-3" /> {ev.unique_users}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
